import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineCameraIcon from 'LuxKit/Icons/line/LineCameraIcon'
import LineMobileAndroidIcon from 'LuxKit/Icons/line/LineMobileAndroidIcon'

export const TIER_PROGRESSION_BENEFIT_ORDER: Array<App.LoyaltyBenefitType> = [
  'hotel_upgrade',
  'lounge_pass',
  'airport_transfer',
  'discounted_insurance',
  'tour_exp_credit',
  'travelshoot_credit',
  'phone_support',
  'esim_package',
]

export const BENEFIT_ICONS: Record<App.LoyaltyBenefitType, React.ReactNode> = {
  hotel_upgrade: <LineCrownIcon size="XS" />,
  airport_transfer: <LineTaxiIcon size="XS" />,
  lounge_pass: <LineChairWithAirplaneIcon size="XS" />,
  discounted_insurance: <LineShieldCheckIcon size="XS" />,
  phone_support: <LinePhoneIcon size="XS" />,
  tour_exp_credit: <LineCompassIcon size="XS" />,
  travelshoot_credit: <LineCameraIcon size="XS" />,
  early_access: null,
  esim_package: <LineMobileAndroidIcon size="XS" />,
  hidden_access: null,
}

interface Props {
  benefit: App.LoyaltyRegionTierBenefit
}

function TierProgressionBenefitTitle({ benefit }: Props): React.ReactElement {
  const { code, limit, unlimited, value } = benefit
  const count = limit ?? 1

  switch (code) {
    case 'hotel_upgrade':
      if (unlimited) {
        return (
          <FormattedMessage
            id="IaFiELEGqzLc2O8xNkZe"
            defaultMessage="Unlimited free room or package upgrades"
            description="Benefit title for unlimited room upgrades on the room page"
          />
        )
      }
      return (
        <FormattedMessage
          id="yC0WsNLD_Ob456YVXBe8"
          defaultMessage="{count, plural, one {1x Free room or package upgrade} other {#x Free room or package upgrades}}"
          description="Benefit title for limited room upgrades on the hotel or room booking page"
          values={{ count }}
        />
      )
    case 'airport_transfer':
      if (unlimited) {
        return (
          <FormattedMessage
            id="yKDIK0jxc6_Swu5f_nwH"
            defaultMessage="Unlimited free airport chauffeur service"
            description="Benefit title for unlimited airport transfers in the loyalty program section"
          />
        )
      }
      return (
        <FormattedMessage
          id="My3cDInErtzR_fOMQ4On"
          defaultMessage="{count, plural, one {Free airport chauffeur service} other {#x Free airport chauffeur service}}"
          description="Benefit title for limited airport transfers for transfers"
          values={{ count }}
        />
      )
    case 'lounge_pass':
      if (unlimited) {
        return (
          <FormattedMessage
            id="dHrlSrs_eiKFyqKId2t6"
            defaultMessage="Unlimited free airport lounge passes"
            description="Benefit title for unlimited lounge passes in the benefits list"
          />
        )
      }
      return (
        <FormattedMessage
          id="Hjj8Q_UvNoaV5TYidvQE"
          defaultMessage="{count, plural, one {Free airport lounge pass} other {#x Free airport lounge passes}}"
          description="Benefit title for limited lounge passes in the loyalty benefits section"
          values={{ count }}
        />
      )
    case 'discounted_insurance':
      return (
        <FormattedMessage
          id="HFVzqpSMkiuiw9upEzVF"
          defaultMessage="{percent}% Travel protection discount"
          description="Benefit title for travel insurance discount in the insurance flow"
          values={{ percent: value?.amount ?? 0 }}
        />
      )
    case 'phone_support':
      return (
        <FormattedMessage
          id="6VsYcSQ0vwKmoHOqfYpH"
          defaultMessage="Priority phone support 24/7"
          description="Benefit title for priority phone support in the support area"
        />
      )
    case 'tour_exp_credit':
      return (
        <FormattedMessage
          id="n92hY4GKhaKiBWL83Fwu"
          defaultMessage="{count, plural, one {<currency></currency> Tour optional extra credit} other {#x <currency></currency> Tour optional extra credits}}"
          description="Benefit title for tour experience credits on the tour page"
          values={{
            count,
            currency: () => <FormatCurrency value={value?.amount ?? 0} />,
          }}
        />
      )
    case 'travelshoot_credit':
      return (
        <FormattedMessage
          id="Tjg9BJ96AVsv3DsZy8Di"
          defaultMessage="<currency></currency> TravelShoot voucher"
          description="Benefit title for TravelShoot voucher for promotional codes or vouchers"
          values={{
            currency: () => <FormatCurrency value={value?.amount ?? 0} />,
          }}
        />
      )
    case 'esim_package':
      return (
        <FormattedMessage
          id="votTmCrdVG_I5jd5Ohcp"
          defaultMessage="{value}{unit} eSIM per international trip"
          description="Benefit title for free eSIM package that provides global data connectivity for travelers"
          values={{
            value: value?.amount ?? 0,
            unit: value?.unit ?? 'GB',
          }}
        />
      )
    default:
      return <>{code}</>
  }
}

export default TierProgressionBenefitTitle
